import type { MapViewState, SavedTarget, SessionData } from "../types";

const SESSION_PREFIX = "azimetria.session.v1.";
const LEGACY_SESSION_KEY = "azimetria.session.v1";
const LEGACY_TARGETS_KEY = "azimetria.targets.v1";
const MAP_KEY = "azimetria.map.v1";
const USER_ID_KEY = "azimetria.user.v1";
const CALLSIGN_KEY = "azimetria.callsign.v1";
const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const sessionKey = (sessionId: string) =>
  `${SESSION_PREFIX}${sessionId.toUpperCase()}`;

export const loadSession = (sessionId: string): SessionData | null => {
  const raw = localStorage.getItem(sessionKey(sessionId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionData;
  } catch {
    return null;
  }
};

export const saveSession = (sessionId: string, session: SessionData) => {
  localStorage.setItem(sessionKey(sessionId), JSON.stringify(session));
};

export const clearSession = (sessionId: string) => {
  localStorage.removeItem(sessionKey(sessionId));
  localStorage.removeItem(LEGACY_TARGETS_KEY);
  localStorage.removeItem(LEGACY_SESSION_KEY);
};

export const loadLegacyTargets = (): SavedTarget[] => {
  const raw = localStorage.getItem(LEGACY_TARGETS_KEY);
  if (!raw) return [];
  try {
    const targets = JSON.parse(raw) as SavedTarget[];
    localStorage.removeItem(LEGACY_TARGETS_KEY);
    return targets;
  } catch {
    return [];
  }
};

export const loadLegacySession = (): SessionData | null => {
  const raw = localStorage.getItem(LEGACY_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionData;
  } catch {
    return null;
  }
};

export const loadMapView = (): MapViewState | null => {
  const raw = localStorage.getItem(MAP_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as MapViewState;
  } catch {
    return null;
  }
};

export const saveMapView = (view: MapViewState) => {
  localStorage.setItem(MAP_KEY, JSON.stringify(view));
};

export const loadUserId = (): string | null =>
  localStorage.getItem(USER_ID_KEY);

export const saveUserId = (userId: string) => {
  localStorage.setItem(USER_ID_KEY, userId);
};

export const isValidAnonymousUserId = (
  value: string | null | undefined
): value is string => typeof value === "string" && UUID_V4_REGEX.test(value);

const generateAnonymousUserId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;

export const loadOrCreateUserId = () => {
  const stored = loadUserId();
  if (isValidAnonymousUserId(stored)) {
    return stored;
  }
  const next = generateAnonymousUserId();
  saveUserId(next);
  return next;
};

export const loadCallsign = (): string | null =>
  localStorage.getItem(CALLSIGN_KEY);

export const saveCallsign = (callsign: string) => {
  localStorage.setItem(CALLSIGN_KEY, callsign);
};
